import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import {
  getConnectionState,
  subscribeConnection,
  type ConnectionState,
} from '../../services/auth/refreshCoordinator';
import { peekSmsReauth, subscribeSmsReauth } from '../../services/auth/reauthBus';
import { useAuth } from '../../context/AuthContext';
import styled from 'styled-components';

interface ProtectedRouteProps {
  requireAuth?: boolean;
  roles?: string[];
}

// 스타일드 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: #f5f5f5;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #4a6cfa;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin-top: 20px;
  font-size: 16px;
  color: #555;
`;

/**
 * 인증 상태 또는 특정 역할을 요구하는 보호된 라우트 컴포넌트
 *
 * @param requireAuth 인증이 필요한지 여부 (기본값: true)
 * @param roles 접근 권한이 있는 역할 목록
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requireAuth = true, roles = [] }) => {
  const { state } = useAuth();
  const location = useLocation();
  const [authCheckTimeout, setAuthCheckTimeout] = useState(false);

  // 개발 환경에서는 컴포넌트 테스트를 위해 인증 우회
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isComponentsDemo = location.pathname.includes('/components/demo');
  const bypassAuth = isDevelopment && isComponentsDemo;

  // ✅ 무한 로딩 방지: 토큰이 있지만 인증 상태가 업데이트되지 않는 경우 타임아웃 처리
  const hasToken = !!localStorage.getItem('auth_token');

  // ⛔ 서버 연결이 끊긴 상태에서는 새로고침하지 않는다 — 최종 심사 [F-9].
  //
  //    새로고침은 「토큰은 있는데 인증이 안 섰다 = 화면 상태가 꼬였다」를 푸는 장치다.
  //    그런데 서버 장애로 갱신을 못 한 경우에도 같은 모양이 된다. 그때 3초마다 새로고침하면
  //    「다시 로그인해 주세요」 안내가 뜰 새도 없이 화면이 계속 깜빡이고,
  //    새로고침마다 요청이 나가 이미 힘든 서버를 더 두드린다.
  const [연결, set연결] = useState<ConnectionState>(getConnectionState());
  useEffect(() => subscribeConnection(set연결), []);

  // ⛔ SMS 재인증 창이 떠 있으면 **절대 새로고침하지 않는다** — 2026-09-24 운영 실사용 결함.
  //
  //    재인증 대기는 아래 조건과 **모양이 똑같다**:
  //      토큰은 있고(일부러 안 지운다) · 인증은 안 섰고 · 서버는 멀쩡히 403 을 답한다(연결 ok).
  //    그래서 이 장치가 **3초마다 화면을 새로고침**했다. 운영 로그에서 재발급이 4초 간격으로
  //    수 분간 반복된 것이 이 타이머다.
  //
  //    사람 쪽에서 보면: 인증번호 발송을 누르면 화면이 새로고침되고 입력창이 사라진다.
  //    창이 다시 떠도 3초 뒤 또 새로고침되므로 **인증번호를 넣을 시간이 없다.**
  //    영영 인증을 마칠 수 없고, 로그아웃 말고는 길이 없다.
  //
  //    챌린지를 고정하는 수정(reauthBus)만으로는 이것을 못 막는다 — 새로고침은
  //    모듈 상태까지 통째로 날리기 때문이다. 두 곳을 함께 막아야 한다.
  //
  //    ⚠ 인증을 마치면 창이 닫히고 이 값이 false 가 되므로, 그때는 새로고침이
  //      원래 목적대로(꼬인 화면 상태 복구) 다시 동작한다.
  const [재인증대기, set재인증대기] = useState<boolean>(() => !!peekSmsReauth());
  useEffect(() => subscribeSmsReauth((요청) => set재인증대기(!!요청)), []);

  useEffect(() => {
    // 토큰이 있고 인증되지 않은 상태에서 로딩이 아닌 경우에만 타임아웃 설정
    if (연결 !== 'ok') return;
    if (재인증대기) return;
    if (hasToken && !state.isAuthenticated && !state.isLoading) {
      console.log('[ProtectedRoute] 토큰은 있지만 인증 상태 false - 3초 후 페이지 새로고침');
      const timeout = setTimeout(() => {
        console.log('[ProtectedRoute] 타임아웃 - 페이지 새로고침으로 AuthContext 재초기화');
        setAuthCheckTimeout(true);
        // 페이지 새로고침으로 AuthContext initAuth 재실행
        window.location.reload();
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [hasToken, state.isAuthenticated, state.isLoading, 연결, 재인증대기]);

  // AuthContext에서 이미 토큰 갱신을 처리하므로 여기서는 별도 처리 불필요

  // components/demo 경로이면서 개발 환경일 경우 인증 체크 우회
  if (bypassAuth) {
    return <Outlet />;
  }

  // 로딩 중이면 로딩 표시
  if (state.isLoading) {
    return (
      <LoadingContainer>
        <div>
          <Spinner />
          <LoadingText>로딩 중...</LoadingText>
        </div>
      </LoadingContainer>
    );
  }

  // 인증이 필요한 경우
  if (requireAuth) {
    // 인증되지 않았고 토큰도 없으면 로그인 페이지로 리디렉션
    if (!state.isAuthenticated && !hasToken) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // 토큰은 있지만 아직 인증 상태가 업데이트되지 않은 경우 (AuthContext 초기화 대기)
    // isLoading 체크 제거하여 localStorage 토큰이 있으면 무조건 AuthContext 초기화 대기
    if (hasToken && !state.isAuthenticated) {
      return (
        <LoadingContainer>
          <div>
            <Spinner />
            <LoadingText>인증 정보 확인 중...</LoadingText>
          </div>
        </LoadingContainer>
      );
    }

    // 역할 기반 접근 제어 (특정 역할이 필요한 경우)
    if (roles.length > 0 && state.user) {
      const hasRequiredRole = roles.includes(state.user.role);

      if (!hasRequiredRole) {
        // 권한이 없으면 접근 거부 페이지로 리디렉션
        return <Navigate to="/access-denied" replace />;
      }
    }
  } else {
    // 인증이 필요하지 않은 경우 (예: 로그인/회원가입 페이지)
    // 이미 인증된 사용자는 홈 페이지로 리디렉션
    if (state.isAuthenticated) {
      return <Navigate to="/" replace />;
    }
  }

  // 접근이 허용된 경우 자식 라우트 렌더링
  return <Outlet />;
};

export default ProtectedRoute;
